import { Authenticated, CanParams, Refine } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";

import {
  ErrorComponent,
  notificationProvider,
  RefineSnackbarProvider
} from "@refinedev/mui";

import { CssBaseline, GlobalStyles } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CanAccess } from "@refinedev/core";
import routerBindings, {
  CatchAllNavigate,
  NavigateToResource,
  UnsavedChangesNotifier
} from "@refinedev/react-router-v6";
import AnalyticsWatchEdit from "pages/analyticsWatchLists/edit";
import AnalyticsWatchList from "pages/analyticsWatchLists/list";
import { DelistPositionsList } from "pages/delistPositions";
import { BadgeCreate, BadgeEdit, BadgesList } from "pages/badges";
import {
  BadgeTypeCreate,
  BadgeTypeEdit,
  BadgeTypeList
} from "pages/badgetypes";
import { BadwordsCreate, BadwordsList } from "pages/badwords";
import {
  CommonReasonsCreate,
  CommonReasonsEdit,
  CommonReasonsList
} from "pages/commonReasons";
import {
  FollowersSettingsCreate,
  FollowersSettingsEdit,
  FollowersSettingsList
} from "pages/followersSettings";
import { Login } from "pages/login";
import { MessageIncidentsList } from "pages/messageIncidents";
import { OrcaDeployActivityLogsList } from "pages/orcaDeployActivityLogs";
import { OrcaDeploymentList } from "pages/orcaDeployment";
import {
  ProdCategoriesCreate,
  ProdCategoriesEdit,
  ProdCategoriesEdit2,
  ProdCategoriesEdit3, ProdCategoriesEditPackage, ProdCategoriesList
} from "pages/prodCategories";
import {
  ProductsCreate,
  ProductsEdit,
  ProductsEdit2,
  ProductsEdit3,
  ProductsList
} from "pages/products";
import {
  QuotestreamPackageCreate,
  QuotestreamPackageEdit,
  QuotestreamPackageList
} from "pages/quotestreamPackages";
import { ReportedMessagesList } from "pages/reportedMessages";
import { ReviewsList } from "pages/reviews";
import {
  RoleBadgesAssignmentsCreate,
  RoleBadgesAssignmentsEdit,
  RoleBadgesAssignmentsList
} from "pages/roleBadgesAssignments";
import { RoomsCreate, RoomsEdit, RoomsList } from "pages/rooms";
import { ScannerConfigChangesList, ScannerConfigChangesShow } from "pages/ScannerConfigChanges";
import { SettingsList } from "pages/settings";
import {
  StreamVendorsCreate,
  StreamVendorsEdit,
  StreamVendorsList
} from "pages/streamVendors";
import { SymbolCreate, SymbolsList } from "pages/symbols";
import { TicketsEdit, TicketsList } from "pages/tickets";
import { UserBadgesCreate, UserBadgesList } from "pages/userBadges";
import { UserCreate, UserEdit, UserList } from "pages/users";
import { WelcomeList } from "pages/welcome";
import { useCallback } from "react";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { authProvider } from "./authProvider";
import { ThemedLayout } from "./components/themedLayout";
import { ThemedHeader } from "./components/themedLayout/header";
import { ThemedSider } from "./components/themedLayout/sider";
import { ThemedTitle } from "./components/themedLayout/title";
import { config } from "./config";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { dataProvider } from "./rest-data-provider/index";

function App() {
  const apiUrl = config().server.api;
  const scannerUrl = config().server.stock;

  const getIsAdmin = useCallback(() => {
    const userData = JSON.parse(
      window.sessionStorage.getItem("user") as string
    );
    if (userData !== null) {
      return userData.group === 1;
    } else {
      return false;
    }
  }, []);
  const getAuthorityEnabled = useCallback(() => {
    const userData = JSON.parse(
      window.sessionStorage.getItem("user") as string
    );
    let authorityObj = userData && userData?.authority;
    let authorityKey = authorityObj && Object.keys(authorityObj);
    if (userData !== null) {
      return authorityKey
        .filter((authorityKey: any) => authorityObj[authorityKey] === true)
        .map((item: any) => {
          switch (item) {
            case "badges":
              return "badge";
            case "badge-types":
              return "badge-type";
            case "badge-rules":
              return "followers-settings";
            case "uri-reviews":
              return "review";
            case "role-badge-relations":
              return "role-badges-assignments";
            case "users":
              return "user";
            case "prod-categories":
              return "product-categories";
            default:
              return item;
          }
        });
    } else {
      return ["home"];
    }
  }, []);

  const handleMenuAuthority = (resource?: string): { can: boolean } => {
    if (resource === "welcome") return { can: true };

    if (resource === "badge-type") {
      const res = getAuthorityEnabled().includes("badge");
      return { can: res };
    }

    if (resource?.toLocaleLowerCase() === "analyticswatchlists") {
      const res = getAuthorityEnabled().includes("settings");
      return { can: res };
    }

    const isInclude = getAuthorityEnabled().includes(resource);
    return { can: isInclude };
  };

  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ColorModeContextProvider>
            <CssBaseline />
            <GlobalStyles styles={{ html: { WebkitFontSmoothing: "auto" } }} />
            <RefineSnackbarProvider>
              <Refine
                dataProvider={{
                  default: dataProvider(apiUrl),
                  scanner: dataProvider(scannerUrl),
                }}
                notificationProvider={notificationProvider}
                routerProvider={routerBindings}
                authProvider={authProvider}
                accessControlProvider={{
                  can: async ({ resource, action }: CanParams) => {
                    if (getIsAdmin() && action === "list") {
                      const res = handleMenuAuthority(resource);
                      return res;
                    } else {
                      return { can: true };
                    }
                  },
                }}
                resources={[
                  {
                    name: "welcome",
                    list: "/welcome",
                  },
                  {
                    name: "badge",
                    list: "/badges",
                    create: "/badges/create",
                    edit: "/badges/edit/:id",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "badge-type",
                    list: "/badgeTypes",
                    create: "/badgeTypes/create",
                    edit: "/badgeTypes/edit/:id",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "bad-words",
                    list: "/badwords",
                    create: "/badwords/create",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "common-reasons",
                    list: "/commonReasons",
                    create: "/commonReasons/create",
                    edit: "/commonReasons/edit/:id",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "followers-settings",
                    list: "/followersSettings",
                    create: "/followersSettings/create",
                    edit: "/followersSettings/edit/:id",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "message-incidents",
                    list: "/messageIncidents",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "products",
                    list: "/products",
                    create: "/products/create",
                    edit: "/products/edit/:id",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "quotestream-packages",
                    list: "/quotestreamPackages",
                    create: "/quotestreamPackages/create",
                    edit: "/quotestreamPackages/edit/:id",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "product-categories",
                    list: "/prodCategories",
                    create: "/prodCategories/create",
                    edit: "/prodCategories/edit/:id",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "reported-messages",
                    list: "/reportedMessages",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "review",
                    list: "/reviews",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "role-badges-assignments",
                    list: "/roleBadgesAssignments",
                    create: "/roleBadgesAssignments/create",
                    edit: "/roleBadgesAssignments/edit/:id",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "rooms",
                    list: "/rooms",
                    create: "/rooms/create",
                    edit: "/rooms/edit/:id",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "settings",
                    list: "/settings",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "stream-vendors",
                    list: "/streamVendors",
                    create: "/streamVendors/create",
                    edit: "/streamVendors/edit/:id",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "tickets",
                    list: "/tickets",
                    edit: "/tickets/edit/:id",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "user",
                    list: "/user",
                    create: "/user/create",
                    edit: "/user/edit/:id",
                    show: "/user/show/:id",
                  },
                  {
                    name: "user-badges",
                    list: "/userBadges",
                    create: "/userBadges/create",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "symbols",
                    list: "/symbols",
                    create: "/symbols/create",
                    meta: {
                      dataProviderName: "scanner",
                      canDelete: true,
                    },
                  },
                  {
                    name: "scanner-config-changes",
                    list: "/scannerConfigChanges",
                    show: "/scannerConfigChanges/show/:id",
                    meta: {
                      dataProviderName: "scanner",
                      canDelete: false,
                    },
                  },
                  {
                    name: "analyticsWatchLists",
                    list: "/analyticsWatchLists",
                    edit: "/analyticsWatchLists/edit",
                    create: "/analyticsWatchLists/create",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "delist-positions",
                    list: "/delistPositions",
                    meta: {
                      canDelete: false,
                    },
                  },
                  {
                    name: "orca-deployer",
                    list: "/orcaDeployment",
                    meta: {
                      label: "Orca Deployment",
                      canDelete: false,
                    },
                  },
                  {
                    name: "orca-developer",
                    list: "/orcaDeployActivityLogs",
                    meta: {
                      label: "Orca Deploy Activity Logs",
                      canDelete: false,
                    },
                  },
                ]}
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                }}
              >
                <Routes>
                  <Route
                    element={
                      <Authenticated
                        fallback={<CatchAllNavigate to="/login" />}
                      >
                        <ThemedLayout
                          Header={ThemedHeader}
                          Sider={ThemedSider}
                          Title={ThemedTitle}
                        >
                          <CanAccess>
                            <Outlet />
                          </CanAccess>
                        </ThemedLayout>
                      </Authenticated>
                    }
                  >
                    <Route
                      index
                      element={<NavigateToResource resource="welcome" />}
                    />
                    <Route path="welcome">
                      <Route index element={<WelcomeList />} />
                    </Route>
                    <Route element={<NavigateToResource resource="badge" />} />
                    <Route path="badges">
                      <Route index element={<BadgesList />} />
                      <Route path="create" element={<BadgeCreate />} />
                      <Route path="edit/:id" element={<BadgeEdit />} />
                    </Route>
                    <Route
                      element={<NavigateToResource resource="symbols" />}
                    />
                    <Route path="symbols">
                      <Route index element={<SymbolsList />} />
                      <Route path="create" element={<SymbolCreate />} />
                    </Route>
                    <Route
                      element={<NavigateToResource resource="scanner-config-changes" />}
                    />
                    <Route path="scannerConfigChanges">
                      <Route index element={<ScannerConfigChangesList />} />
                      <Route path="show/:id" element={<ScannerConfigChangesShow />} />
                    </Route>
                    <Route
                      element={<NavigateToResource resource="badge-type" />}
                    />
                    <Route path="badgeTypes">
                      <Route index element={<BadgeTypeList />} />
                      <Route path="edit/:id" element={<BadgeTypeEdit />} />
                      <Route path="create" element={<BadgeTypeCreate />} />
                    </Route>
                    <Route
                      element={<NavigateToResource resource="badwords" />}
                    />
                    <Route path="badwords">
                      <Route index element={<BadwordsList />} />
                      <Route path="create" element={<BadwordsCreate />} />
                    </Route>
                    <Route
                      element={<NavigateToResource resource="commonReasons" />}
                    />
                    <Route path="commonReasons">
                      <Route index element={<CommonReasonsList />} />
                      <Route path="create" element={<CommonReasonsCreate />} />
                      <Route path="edit/:id" element={<CommonReasonsEdit />} />
                    </Route>
                    <Route
                      element={
                        <NavigateToResource resource="followers-settings" />
                      }
                    />
                    <Route path="followersSettings">
                      <Route index element={<FollowersSettingsList />} />
                      <Route
                        path="create"
                        element={<FollowersSettingsCreate />}
                      />
                      <Route
                        path="edit/:id"
                        element={<FollowersSettingsEdit />}
                      />
                    </Route>
                    <Route
                      element={
                        <NavigateToResource resource="message-incidents" />
                      }
                    />
                    <Route path="messageIncidents">
                      <Route index element={<MessageIncidentsList />} />
                    </Route>
                    <Route
                      element={<NavigateToResource resource="products" />}
                    />
                    <Route path="products">
                      <Route index element={<ProductsList />} />
                      <Route path="create" element={<ProductsCreate />} />
                      <Route
                        path="edit/:id"
                        element={
                          <>
                            <ProductsEdit />
                            <ProductsEdit2 />
                            <ProductsEdit3 />
                          </>
                        }
                      />
                    </Route>
                    <Route
                      element={
                        <NavigateToResource resource="quotestream-packages" />
                      }
                    />
                    <Route path="quotestreamPackages">
                      <Route index element={<QuotestreamPackageList />} />
                      <Route
                        path="create"
                        element={<QuotestreamPackageCreate />}
                      />
                      <Route
                        path="edit/:id"
                        element={<QuotestreamPackageEdit />}
                      />
                    </Route>
                    <Route
                      element={
                        <NavigateToResource resource="prod-categories" />
                      }
                    />
                    <Route path="prodCategories">
                      <Route index element={<ProdCategoriesList />} />
                      <Route path="create" element={<ProdCategoriesCreate />} />
                      <Route
                        path="edit/:id"
                        element={
                          <>
                            <ProdCategoriesEdit />
                            <ProdCategoriesEdit2 />
                            <ProdCategoriesEdit3 />
                            <ProdCategoriesEditPackage />
                          </>
                        }
                      />
                    </Route>

                    <Route
                      element={
                        <NavigateToResource resource="reported-messages" />
                      }
                    />
                    <Route path="reportedMessages">
                      <Route index element={<ReportedMessagesList />} />
                    </Route>
                    <Route element={<NavigateToResource resource="review" />} />
                    <Route path="reviews">
                      <Route index element={<ReviewsList />} />
                    </Route>
                    <Route
                      element={
                        <NavigateToResource resource="role-badges-assignments" />
                      }
                    />
                    <Route path="roleBadgesAssignments">
                      <Route index element={<RoleBadgesAssignmentsList />} />
                      <Route
                        path="create"
                        element={<RoleBadgesAssignmentsCreate />}
                      />
                      <Route
                        path="edit/:id"
                        element={<RoleBadgesAssignmentsEdit />}
                      />
                    </Route>
                    <Route
                      element={<NavigateToResource resource="stream-vendors" />}
                    />
                    <Route path="streamVendors">
                      <Route index element={<StreamVendorsList />} />
                      <Route path="create" element={<StreamVendorsCreate />} />
                      <Route path="edit/:id" element={<StreamVendorsEdit />} />
                    </Route>
                    <Route element={<NavigateToResource resource="rooms" />} />
                    <Route path="rooms">
                      <Route index element={<RoomsList />} />
                      <Route path="create" element={<RoomsCreate />} />
                      <Route path="edit/:id" element={<RoomsEdit />} />
                    </Route>
                    <Route
                      element={<NavigateToResource resource="settings" />}
                    />
                    <Route path="settings">
                      <Route index element={<SettingsList />} />
                    </Route>
                    <Route
                      index
                      element={<NavigateToResource resource="tickets" />}
                    />
                    <Route path="tickets">
                      <Route index element={<TicketsList />} />
                      <Route path="edit/:id" element={<TicketsEdit />} />
                    </Route>
                    <Route element={<NavigateToResource resource="user" />} />
                    <Route path="user">
                      <Route index element={<UserList />} />
                      <Route path="edit/:id" element={<UserEdit />} />
                      <Route path="create" element={<UserCreate />} />
                    </Route>
                    <Route path="userBadges">
                      <Route index element={<UserBadgesList />} />
                      <Route path="create" element={<UserBadgesCreate />} />
                    </Route>
                    <Route path="analyticsWatchLists">
                      <Route index element={<AnalyticsWatchList />} />
                      <Route path="edit" element={<AnalyticsWatchEdit />} />
                      <Route path="create" element={<AnalyticsWatchEdit />} />
                    </Route>
                    <Route path="delistPositions">
                      <Route index element={<DelistPositionsList />} />
                    </Route>
                    <Route
                      element={
                        <NavigateToResource resource="orca-deployer" />
                      }
                    />
                    <Route path="orcaDeployment">
                      <Route index element={<OrcaDeploymentList />} />
                    </Route>
                    <Route
                      element={
                        <NavigateToResource resource="orca-developer" />
                      }
                    />
                    <Route path="orcaDeployActivityLogs">
                      <Route index element={<OrcaDeployActivityLogsList />} />
                    </Route>
                  </Route>
                  <Route
                    element={
                      <Authenticated fallback={<Outlet />}>
                        <NavigateToResource resource="welcome" />
                      </Authenticated>
                    }
                  >
                    <Route path="/login" element={<Login />} />
                  </Route>
                  <Route
                    element={
                      <Authenticated>
                        <ThemedLayout Header={ThemedHeader}>
                          <Outlet />
                        </ThemedLayout>
                      </Authenticated>
                    }
                  >
                    <Route path="*" element={<ErrorComponent />} />
                  </Route>
                </Routes>

                <RefineKbar />
                <UnsavedChangesNotifier />
              </Refine>
            </RefineSnackbarProvider>
          </ColorModeContextProvider>
        </LocalizationProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
